// Dashboard · Profile form. Extracted so profile persistence + language
// controls can evolve without touching the leads table or billing wiring.
import React, { useMemo } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import MultiSelectChips from "../MultiSelectChips";
import { LANGUAGES, langName } from "../../lib/languages";
import { Field } from "./primitives";

export default function ProfileTab({ profile, setProfile, catalog, saving, onSave }) {
  // Only recompute when the primary language changes — this used to run on
  // every parent re-render (typing in the biography field would cost 40+
  // filter+map ops for the language list).
  const additionalLanguageOptions = useMemo(
    () => LANGUAGES.filter((l) => l.code !== profile.primary_language).map((l) => l.code),
    [profile.primary_language]
  );

  return (
    <Card className="card-elevated p-7 grid md:grid-cols-2 gap-5">
      <Field label="Full name"><Input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></Field>
      <Field label="Company"><Input data-testid="profile-company" value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} /></Field>
      <Field label="WhatsApp"><Input data-testid="profile-whatsapp" value={profile.whatsapp} onChange={(e) => setProfile({ ...profile, whatsapp: e.target.value })} /></Field>
      <Field label="Booking link"><Input data-testid="profile-booking" value={profile.booking_link} onChange={(e) => setProfile({ ...profile, booking_link: e.target.value })} /></Field>
      <Field label="Profile photo URL"><Input data-testid="profile-photo" value={profile.profile_photo} onChange={(e) => setProfile({ ...profile, profile_photo: e.target.value })} /></Field>

      <div className="md:col-span-2">
        <Field label="Public URL slug (this is your sharable link)">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm whitespace-nowrap">bizpulseai.net/c/</span>
            <Input
              data-testid="profile-slug"
              value={profile.slug}
              onChange={(e) => setProfile({ ...profile, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
              placeholder="capital-bay-consulting"
            />
          </div>
        </Field>
        <p className="text-xs text-muted-foreground mt-2">
          If your desired slug is taken, we&apos;ll auto-append <code>-2</code>, <code>-3</code>, … and show you the final assigned value.
        </p>
      </div>

      <div className="md:col-span-2">
        <Field label="Biography">
          <Textarea data-testid="profile-bio" rows={4} value={profile.biography} onChange={(e) => setProfile({ ...profile, biography: e.target.value })} />
        </Field>
      </div>
      <div className="md:col-span-2"><Field label="Expertise"><MultiSelectChips testid="profile-expertise" value={profile.expertise} onChange={(v) => setProfile({ ...profile, expertise: v })} allowFree /></Field></div>
      <div className="md:col-span-2"><Field label="Countries"><MultiSelectChips testid="profile-countries" value={profile.countries} onChange={(v) => setProfile({ ...profile, countries: v })} options={catalog.countries} /></Field></div>
      <div className="md:col-span-2"><Field label="Industries"><MultiSelectChips testid="profile-industries" value={profile.industries} onChange={(v) => setProfile({ ...profile, industries: v })} options={catalog.industries} /></Field></div>
      <div className="md:col-span-2"><Field label="Functions"><MultiSelectChips testid="profile-functions" value={profile.functions} onChange={(v) => setProfile({ ...profile, functions: v })} options={catalog.functions} /></Field></div>

      {/* Pack 7 · Language management. Guards prevent promoting the primary
          into the additional list (or vice versa) which would create a
          duplicate in supported_languages after the server-side backfill. */}
      <div className="md:col-span-2 rounded-md border border-border/60 bg-secondary/30 p-4" data-testid="profile-languages-panel">
        <div className="overline">Languages</div>
        <p className="text-xs text-muted-foreground mt-1">
          The languages you can host a Business Growth Discovery™ in. Your primary is the default for every new campaign.
        </p>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <Field label="Primary language">
            <Select
              value={profile.primary_language}
              onValueChange={(v) => setProfile({
                ...profile,
                primary_language: v,
                additional_languages: (profile.additional_languages || []).filter((l) => l !== v),
              })}
            >
              <SelectTrigger data-testid="profile-primary-language">
                <SelectValue placeholder="Choose">{langName(profile.primary_language)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => (
                  <SelectItem key={l.code} value={l.code} data-testid={`profile-primary-lang-opt-${l.code}`}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Additional languages">
            <MultiSelectChips
              testid="profile-additional-languages"
              value={profile.additional_languages}
              onChange={(v) => setProfile({
                ...profile,
                additional_languages: v.filter((l) => l !== profile.primary_language),
              })}
              options={additionalLanguageOptions}
              renderLabel={langName}
            />
          </Field>
        </div>
      </div>

      <div className="md:col-span-2 flex justify-end">
        <Button
          data-testid="profile-save"
          onClick={onSave}
          disabled={saving}
          className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-7"
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </Card>
  );
}
